import { Link } from 'react-router';
import type { ReactNode } from 'react';
import type { PaymentState, ReconciliationStatus } from '../api/types';
import { useI18n } from '../i18n';
import { errorMessageKey } from '../lib/errors';
import { formatPrice } from '../lib/money';
import { usePanel } from '../state/merchant';

export type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'accent';

/** A small status label. The colour is never the only signal: the text says it too. */
export function Chip({ tone, children, title }: { tone: Tone; children: ReactNode; title?: string }) {
  return (
    <span className={`chip chip--${tone}`} title={title}>
      {children}
    </span>
  );
}

export const PAYMENT_STATE_TONE: Record<PaymentState, Tone> = {
  CREATED: 'warning',
  AWAITING_AUTHORIZATION: 'warning',
  AUTHORIZED: 'info',
  SUBMITTED: 'info',
  SETTLED: 'success',
  REJECTED: 'danger',
  FAILED: 'danger',
  EXPIRED: 'neutral',
  CANCELLED: 'neutral',
};

export const RECONCILIATION_TONE: Record<ReconciliationStatus, Tone> = {
  MATCHED: 'success',
  PENDING: 'info',
  MISSING_IN_BANK: 'danger',
  AMOUNT_MISMATCH: 'warning',
  OTHER_INCOMING: 'accent',
};

export function PaymentStateChip({ state }: { state: PaymentState }) {
  const { t } = useI18n();
  return <Chip tone={PAYMENT_STATE_TONE[state] ?? 'neutral'}>{t(`state.${state}`)}</Chip>;
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        {subtitle ? <p className="page-subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </header>
  );
}

export function Loading({ label }: { label?: string }) {
  const { t } = useI18n();
  return (
    <div className="loading" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <span>{label ?? t('app.loading')}</span>
    </div>
  );
}

export function ErrorNotice({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useI18n();
  return (
    <div className="notice notice--danger" role="alert">
      <p>{t(errorMessageKey(error))}</p>
      {onRetry ? (
        <button type="button" className="btn btn--secondary" onClick={onRetry}>
          {t('app.retry')}
        </button>
      ) : null}
    </div>
  );
}

export type UpsellReason = 'history' | 'export' | 'reconciliation';

/** Shown instead of an error when a feature needs the web panel plan (402 PANEL_REQUIRED). */
export function Upsell({ reason, compact = false }: { reason: UpsellReason; compact?: boolean }) {
  const { t } = useI18n();
  const panel = usePanel();
  const price = formatPrice(panel?.priceMinor ?? 35_000);
  return (
    <section className={`upsell${compact ? ' upsell--compact' : ''}`} aria-labelledby={`upsell-${reason}`} data-testid="upsell">
      <div className="upsell-icon" aria-hidden="true">
        <LockIcon />
      </div>
      <div className="upsell-body">
        <h2 id={`upsell-${reason}`}>{t('upsell.title')}</h2>
        <p>{t(`upsell.${reason}`)}</p>
        <p className="muted">{t('upsell.price', { price })}</p>
      </div>
      <Link className="btn btn--primary" to="/plan">
        {t('upsell.cta')}
      </Link>
    </section>
  );
}

export function StatCard({
  label,
  value,
  hint,
  tone,
  testId,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
  testId?: string;
}) {
  return (
    <div className={`stat${tone ? ` stat--${tone}` : ''}`} data-testid={testId}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {hint ? <div className="stat-hint">{hint}</div> : null}
    </div>
  );
}

export function LockIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <rect x="4" y="10" width="16" height="11" rx="2.5" stroke="currentColor" strokeWidth="2" />
      <path d="M8 10V7a4 4 0 1 1 8 0v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export function ZinqLogo({ size = 32 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true" focusable="false">
      <rect width="64" height="64" rx="14" fill="#0A1F44" />
      <path
        d="M18 20h28l-22 24h22"
        fill="none"
        stroke="#2DD4BF"
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="48" cy="48" r="4" fill="#2DD4BF" />
    </svg>
  );
}
